// src/terminal/commands/auth.js

import { trackEvent } from '../../utils/analytics';
import { verifyPassword } from '../auth';

export function registerAuthCommands(commandMap, fsState, addLines, setTerminalState) {

  commandMap['login'] = (args) => {
    const username = args[0];

    if (!username) {
      addLines([
        { type: 'blank' },
        { type: 'line', text: '  Usage: login <username>', color: '#ffb000' },
        { type: 'blank' },
        { type: 'line', text: '  Available accounts:', color: '#555' },
        { type: 'line', text: '    guest    — Guest access (no password required)', color: '#00ff41' },
        { type: 'line', text: '    skyla    — Technician account', color: '#ffb000' },
        { type: 'line', text: '    rajiv    — IT Support', color: '#ffb000' },
        { type: 'line', text: '    root     — Administrator (restricted)', color: '#ff0040' },
        { type: 'blank' },
      ]);
      return;
    }

    const user = username.toLowerCase();

    if (user === 'guest') {
      const result = fsState.loginAs('guest');
      if (result.success) {
        addLines([
          { type: 'blank' },
          { type: 'line', text: '  ═══════════════════════════════════════', color: '#00ff41' },
          { type: 'blank' },
          { type: 'line', text: '  GUEST ACCESS GRANTED', color: '#00ff41' },
          { type: 'blank' },
          { type: 'line', text: `  ${result.message}`, color: '#cccccc' },
          { type: 'blank' },
          { type: 'line', text: '  Home directory: /home/guest', color: '#555' },
          { type: 'line', text: '  Try: ls, cd, cat, tree, find', color: '#555' },
          { type: 'blank' },
          { type: 'line', text: '  ═══════════════════════════════════════', color: '#00ff41' },
          { type: 'blank' },
        ]);
        setTerminalState(prev => ({ ...prev, user: 'guest' }));
        trackEvent('terminal_login', 'auth', 'guest');
      }
      return;
    }

    if (user === 'dan') {
      addLines([
        { type: 'blank' },
        { type: 'line', text: '  Account disabled. Employee no longer exists.', color: '#ff0040' },
        { type: 'blank' },
        { type: 'line', text: '  🐍', color: '#00ff41' },
        { type: 'blank' },
      ]);
      return;
    }

    // For password-protected accounts, enter password mode
    if (['skyla', 'root', 'admin', 'rajiv'].includes(user)) {

      setTerminalState(prev => ({
        ...prev,
        awaitingLogin: { username: user === 'admin' ? 'root' : user },
      }));

      addLines([
        { type: 'blank' },
        { type: 'line', text: `  Login: ${user}`, color: '#ffb000' },
        { type: 'line', text: '  Enter password:', color: '#ffb000' },
      ]);
      return;
    }

    addLines([
      { type: 'blank' },
      { type: 'line', text: `  Unknown user: ${username}`, color: '#ff0040' },
      { type: 'blank' },
    ]);
  };

  commandMap['logout'] = () => {
    if (!fsState.currentUser) {
      addLines([
        { type: 'blank' },
        { type: 'line', text: '  Not logged in.', color: '#555' },
        { type: 'blank' },
      ]);
      return;
    }

    const user = fsState.currentUser;
    fsState.logout();

    addLines([
      { type: 'blank' },
      { type: 'line', text: `  User "${user}" logged out.`, color: '#ffb000' },
      { type: 'line', text: '  Session terminated. Returning to public access.', color: '#555' },
      { type: 'blank' },
    ]);
    setTerminalState(prev => ({ ...prev, user: null }));
    trackEvent('terminal_logout', 'auth', user);
  };

  commandMap['whoami'] = () => {
    if (fsState.currentUser) {
      const userInfo = {
        guest: { role: 'Guest Operator', clearance: 'Level 1' },
        skyla: { role: 'Technician', clearance: 'Level 2' },
        rajiv: { role: 'IT Support Lead', clearance: 'Level 3' },
        root:  { role: 'Administrator', clearance: 'Level ████' },
      };
      const info = userInfo[fsState.currentUser] || { role: 'Unknown', clearance: 'Unknown' };

      addLines([
        { type: 'blank' },
        { type: 'line', text: `  User:       ${fsState.currentUser}`, color: '#00ff41' },
        { type: 'line', text: `  Role:       ${info.role}`, color: '#cccccc' },
        { type: 'line', text: `  Clearance:  ${info.clearance}`, color: '#cccccc' },
        { type: 'line', text: `  Terminal:   node7.ophidia.internal`, color: '#555' },
        { type: 'line', text: `  Session:    ${new Date().toISOString()}`, color: '#555' },
        { type: 'blank' },
      ]);
    } else {
      addLines([
        { type: 'blank' },
        { type: 'line', text: '  visitor (not authenticated)', color: '#555' },
        { type: 'line', text: '  Type LOGIN GUEST to access the filesystem.', color: '#555' },
        { type: 'blank' },
      ]);
    }
  };
}

// ============================================
// Handle login password input
// Called from Terminal.js when in awaitingLogin mode
// ============================================
export async function handleLoginPassword(cmd, fsState, loginState, addLines, setTerminalState, unlock) {
  const { username } = loginState;
  const password = cmd.trim();

  // Show masked input
  addLines([{ type: 'input', text: `> ${'•'.repeat(cmd.length)}` }]);

  // Determine which hash key to check
  const hashKey = (username === 'admin') ? 'root' : username;

  // Verify password against hash (async)
  const isValid = await verifyPassword(hashKey, password);

  if (isValid) {
    // Password correct — set filesystem access
    const result = fsState.loginAs(username);

    if (username === 'root' || username === 'admin') {
      // Root access — dramatic sequence
      const accessSequence = [
        [0, '', null],
        [200, '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', '#00ff41'],
        [400, '', null],
        [600, '  IDENTITY VERIFIED.', '#00ff41'],
        [900, '  Welcome back, Administrator.', '#00ff41'],
        [1200, '', null],
        [1500, '  ╔═══════════════════════════════════════════╗', '#00ff41'],
        [1600, '  ║  OPHIDIA GLOBAL — NETWORK ADMINISTRATION  ║', '#00ff41'],
        [1700, '  ║  CLEARANCE: LEVEL ████                    ║', '#00ff41'],
        [1800, '  ║  STATUS: COIL PROTOCOL ACTIVE             ║', '#00ff41'],
        [1900, '  ╚═══════════════════════════════════════════╝', '#00ff41'],
        [2200, '', null],
        [2500, '  Home directory: /home/admin', '#00ff41'],
        [2800, '  Try: ls, cat CLASSIFIED.txt', '#00ff41'],
        [3100, '', null],
        [3300, '  The old blood recognizes you.', '#3a8a3a'],
        [3500, '', null],
        [3700, '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', '#00ff41'],
        [3900, '', null],
      ];

      accessSequence.forEach(([delay, text, color]) => {
        setTimeout(() => {
          addLines([color ? { type: 'line', text, color } : { type: 'blank' }]);
        }, delay);
      });

      setTimeout(() => {
        unlock('underbelly');
        setTerminalState(prev => ({ ...prev, user: 'root', awaitingLogin: null }));
      }, 2000);

      trackEvent('easter_egg', 'terminal', 'root_access_granted');
    } else if (username === 'rajiv') {
      // Rajiv login — dramatic IT guy sequence
      const rajivSequence = [
        [0, '', null],
        [200, '  ═══════════════════════════════════════', '#ffb000'],
        [400, '', null],
        [600, '  ACCESS GRANTED — Welcome, Rajiv.', '#00ff41'],
        [900, '', null],
        [1100, '  ╔═══════════════════════════════════════════╗', '#ffb000'],
        [1200, '  ║  OPHIDIA GLOBAL — IT INFRASTRUCTURE       ║', '#ffb000'],
        [1300, '  ║  CLEARANCE: LEVEL 3 (SUPPORT)             ║', '#ffb000'],
        [1400, '  ║  OPU-WEB STATUS: ████████ NOMINAL         ║', '#ffb000'],
        [1500, '  ║  DRONE FLEET: 14,000 ACTIVE               ║', '#ffb000'],
        [1600, '  ║  BUDGET: INSUFFICIENT                     ║', '#ff0040'],
        [1700, '  ╚═══════════════════════════════════════════╝', '#ffb000'],
        [2000, '', null],
        [2300, '  Home directory: /home/rajiv', '#00ff41'],
        [2600, '  12 unresolved tickets. 0 appreciation.', '#555'],
        [2900, '', null],
        [3100, '  ═══════════════════════════════════════', '#ffb000'],
        [3300, '', null],
      ];

      rajivSequence.forEach(([delay, text, color]) => {
        setTimeout(() => {
          addLines([color ? { type: 'line', text, color } : { type: 'blank' }]);
        }, delay);
      });

      setTimeout(() => {
        setTerminalState(prev => ({ ...prev, user: username, awaitingLogin: null }));
      }, 1000);

      trackEvent('terminal_login', 'auth', 'rajiv');

    } else {
      // Normal login success (skyla, etc.)
      addLines([
        { type: 'blank' },
        { type: 'line', text: '  ═══════════════════════════════════════', color: '#00ff41' },
        { type: 'line', text: `  ACCESS GRANTED — Welcome, ${username}`, color: '#00ff41' },
        { type: 'line', text: `  ${result.message || ''}`, color: '#cccccc' },
        { type: 'line', text: `  Home directory: ${fsState.currentPath}`, color: '#555' },
        { type: 'line', text: '  ═══════════════════════════════════════', color: '#00ff41' },
        { type: 'blank' },
      ]);
      setTerminalState(prev => ({ ...prev, user: username, awaitingLogin: null }));
      trackEvent('terminal_login', 'auth', username);
    }
  
  } else {
    // Failed login
    const fakeIP = `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;

    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ AUTHENTICATION FAILED ▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Incorrect password. Your IP has been logged.', color: '#ff0040' },
      { type: 'line', text: `  IP logged: ${fakeIP}`, color: '#555' },
      { type: 'blank' },
    ]);

    setTerminalState(prev => ({ ...prev, awaitingLogin: null }));
    trackEvent('terminal_login_fail', 'auth', username);
  }
}